import { useState, useEffect, useCallback } from 'react'
import { StarIcon, ClockIcon } from '@heroicons/react/outline'
import {
  getChampionshipRankings,
  getUserChampionshipEntry,
  getCurrentWeek,
  ChampionshipEntry,
  WeekInfo,
} from '../../lib/championship'
import { Difficulty } from '../../hooks/gameReducer'
import { DifficultyList } from '../lists/DifficultyList'
import { getUserProfile } from '../../lib/userProfile'
import { DisplayNameInput } from '../profile/DisplayNameInput'

interface Props {
  userId: string
  difficulty: Difficulty
  isVisible: boolean
  showDifficultySelector?: boolean
}

export const ChampionshipContent = ({
  userId,
  difficulty: initialDifficulty,
  isVisible,
  showDifficultySelector = true,
}: Props) => {
  const [rankings, setRankings] = useState<ChampionshipEntry[]>([])
  const [userEntry, setUserEntry] = useState<ChampionshipEntry | null>(null)
  const [userRank, setUserRank] = useState<number | null>(null)
  const [weekInfo, setWeekInfo] = useState<WeekInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedDifficulty, setSelectedDifficulty] =
    useState<Difficulty>(initialDifficulty)

  // Display name state
  const [displayName, setDisplayName] = useState('')

  // Load user's display name when component becomes visible
  useEffect(() => {
    if (isVisible && userId) {
      getUserProfile(userId).then((profile) => {
        if (profile?.displayName) {
          setDisplayName(profile.displayName)
        }
      })
    }
  }, [isVisible, userId])

  const loadChampionshipData = useCallback(async () => {
    try {
      setLoading(true)
      const week = getCurrentWeek()
      setWeekInfo(week)

      const [rankingsData, userEntryData] = await Promise.all([
        getChampionshipRankings(selectedDifficulty),
        getUserChampionshipEntry(userId, selectedDifficulty),
      ])

      setRankings(rankingsData)
      setUserEntry(userEntryData)

      if (userEntryData) {
        const rank = rankingsData.findIndex((entry) => entry.userId === userId)
        setUserRank(rank >= 0 ? rank + 1 : null)
      } else {
        setUserRank(null)
      }
    } catch (err) {
      console.error('Error loading championship data:', err)
    } finally {
      setLoading(false)
    }
  }, [userId, selectedDifficulty])

  useEffect(() => {
    if (isVisible) {
      loadChampionshipData()
    }
  }, [isVisible, loadChampionshipData])

  useEffect(() => {
    setSelectedDifficulty(initialDifficulty)
  }, [initialDifficulty, isVisible])

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('hu-HU', {
      month: 'short',
      day: 'numeric',
    })
  }

  const getTimeUntilReset = () => {
    if (!weekInfo) return ''

    const now = new Date()
    const end = weekInfo.endDate
    const diff = end.getTime() - now.getTime()

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

    if (days > 0) {
      return `${days} nap ${hours} óra`
    }
    return `${hours} óra`
  }

  const getMedalEmoji = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇'
      case 2:
        return '🥈'
      case 3:
        return '🥉'
      default:
        return ''
    }
  }

  return (
    <div className="space-y-4">
      {/* Difficulty selector */}
      {showDifficultySelector && (
        <div className="flex items-center justify-between pb-2 border-b dark:border-gray-600">
          <span className="text-sm font-medium dark:text-white">Szóhossz:</span>
          <DifficultyList
            selected={selectedDifficulty}
            onChange={(value) => setSelectedDifficulty(value)}
          />
        </div>
      )}

      {/* Display name editor */}
      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Neved a ranglistán:
        </label>
        <DisplayNameInput
          userId={userId}
          currentDisplayName={displayName}
          onProfileUpdate={(newName) => {
            setDisplayName(newName)
            loadChampionshipData()
          }}
          compact={true}
        />
      </div>

      {weekInfo && (
        <div className="bg-blue-50 dark:bg-blue-800/50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {weekInfo.weekNumber}. hét
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-200">
                {formatDate(weekInfo.startDate)} -{' '}
                {formatDate(weekInfo.endDate)}
              </p>
            </div>
            <div className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
              <ClockIcon className="w-5 h-5" />
              <span className="text-sm font-medium">{getTimeUntilReset()}</span>
            </div>
          </div>
        </div>
      )}

      {userEntry && (
        <div className="bg-green-50 dark:bg-green-800/50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                A te helyezésed
              </p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                {userRank ? `#${userRank}` : '-'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-700 dark:text-green-300">
                {userEntry.weeklyScore} pont
              </p>
              <p className="text-xs text-green-700 dark:text-green-300">
                {userEntry.gamesWon}/{userEntry.gamesPlayed} győzelem
              </p>
              <p className="text-xs text-green-700 dark:text-green-300">
                Sorozat: {userEntry.currentStreak}
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-semibold mb-3 dark:text-white flex items-center gap-2">
          <StarIcon className="w-5 h-5" />
          Globális Rangsor
        </h3>

        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Betöltés...
          </div>
        ) : rankings.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-300">
            Még nincsenek eredmények ezen a héten
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {rankings.map((entry, index) => {
              const isCurrentUser = entry.userId === userId
              return (
                <div
                  key={entry.userId}
                  className={`flex items-center justify-between p-3 border rounded-lg ${
                    isCurrentUser
                      ? 'bg-blue-50 dark:bg-blue-800/30 border-blue-300 dark:border-blue-500'
                      : 'dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className="font-bold text-lg w-10 flex items-center gap-1 dark:text-white">
                      {getMedalEmoji(index + 1)}
                      {index + 1}.
                    </span>
                    <div className="flex-1">
                      <p className="font-medium dark:text-white">
                        {entry.displayName || 'Anonim'}
                        {isCurrentUser && (
                          <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                            (Te)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {entry.gamesPlayed} játék · {entry.gamesWon} győzelem
                        {entry.currentStreak > 0 &&
                          ` · ${entry.currentStreak} sorozat`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg dark:text-white">
                      {entry.weeklyScore}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      pont
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="border-t dark:border-gray-600 pt-4">
        <h4 className="text-sm font-semibold mb-2 dark:text-white">
          Pontszámítás
        </h4>
        <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
          <li>• Szó megfejtése: 10 pont</li>
          <li>• Kevesebb próbálkozás: +1-5 bónusz pont</li>
          <li>• A rangsor minden héten hétfőn nullázódik</li>
        </ul>
      </div>
    </div>
  )
}
